import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import {
  actions,
  currentAcquisitionAttribution,
  useAppStore,
} from "@/controller";
import { accountEmail, hasAccountIdentity } from "@/auth-identity";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import { CheckIcon, CursorIcon } from "@/components/Icons";
import {
  walkthroughFrame,
  walkthroughStepStart,
  walkthroughStillFrame,
  type DesktopOs,
  type WalkthroughFrame,
  type WalkthroughPhase,
  type WalkthroughStep,
} from "@/desktop-install-walkthrough";
import { useAuth } from "@/models/useAuth";
import { APP_DOWNLOAD_URLS } from "@/product-links";
import { logClientEvent } from "@/telemetry";

const detectedOs = (): DesktopOs =>
  /Windows/i.test(navigator.userAgent) ? "windows" : "mac";

// Gatekeeper names the browser that downloaded the file; matching it makes the
// rehearsal read as a preview of the user's own screen rather than a stock one.
const browserName = (): string | undefined => {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Microsoft Edge";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Chrome\//.test(ua)) return "Google Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return undefined;
};

type StepCopy = { title: string; detail: ReactNode };

// One title and one rich detail per step, per OS. `<strong>` in the catalog
// marks the file name and the exact button labels the user will click.
const installSteps = (
  t: TFunction,
  os: DesktopOs,
  fileName: string,
): StepCopy[] =>
  ([1, 2, 3] as const).map((step) => ({
    title: t(`downloadPage.${os}.step${step}Title`),
    detail: (
      <Trans
        i18nKey={`downloadPage.${os}.step${step}Detail`}
        values={{ fileName }}
        components={{
          strong: (
            <strong className={step === 1 ? "[overflow-wrap:anywhere]" : ""} />
          ),
        }}
      />
    ),
  }));

export default function DownloadPage() {
  const { t } = useTranslation();
  const token = useAppStore((state) => state.userToken);
  const user = useAuth.currentAuthUser(token);
  const account = hasAccountIdentity(user);
  const requested = new URLSearchParams(window.location.search).get("os");
  const initialOs =
    requested === "windows" || requested === "mac" ? requested : detectedOs();
  const [os, setOs] = useState<DesktopOs>(initialOs);
  const started = useRef(false);
  const url = APP_DOWNLOAD_URLS[os];
  const platformName = t(
    os === "mac" ? "downloadPage.platformMac" : "downloadPage.platformWindows",
  );
  const otherPlatformName = t(
    os === "mac" ? "downloadPage.platformWindows" : "downloadPage.platformMac",
  );
  const fileName = decodeURIComponent(
    new URL(url).pathname.split("/").pop() || "1Transcribe installer",
  );
  const steps = installSteps(t, os, fileName);
  const walkthrough = useWalkthrough(os);
  const redirectUrl = useMemo(
    () => `${window.location.pathname}${window.location.search}`,
    [],
  );

  useEffect(() => {
    if (account) return;
    window.location.replace(
      `/auth?source=desktop-download&redirectUrl=${encodeURIComponent(redirectUrl)}`,
    );
  }, [account, redirectUrl]);

  useEffect(() => {
    if (!account || started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    logClientEvent("acquisition.downloadStarted", {
      destination: os,
      surface: "authenticatedDownload",
      intent: params.get("intent"),
      placement: params.get("placement") || undefined,
    });
    const attribution = currentAcquisitionAttribution();
    if (Object.keys(attribution).length > 0) {
      void actions.user.captureAcquisition(attribution);
    }
    const frame = document.createElement("iframe");
    frame.hidden = true;
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("allow", "downloads");
    frame.src = url;
    document.body.append(frame);
    return () => frame.remove();
  }, [account, os, url]);

  if (!account) return null;

  const email = accountEmail(user) ?? "";
  const activeStep = walkthrough.frame.step;

  return (
    <main className="app-page-background relative min-h-[100svh] bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <BackgroundAmbience />

      <div className="relative grid lg:grid-cols-[minmax(0,46fr)_minmax(0,54fr)]">
        <section className="flex flex-col justify-center px-6 py-10 sm:px-10 lg:min-h-[100svh] lg:px-14 lg:py-16 xl:px-20">
          <div className="mx-auto w-full max-w-[540px] lg:mx-0">
            <a
              href="https://1transcribe.com"
              aria-label={t("downloadPage.homeLink")}
              className="inline-flex items-center gap-3 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--focus-ring)]"
            >
              <img
                src="/logo.png"
                alt=""
                className="h-10 w-10 rounded-[12px] shadow-[var(--lift-sm)]"
              />
              <span className="text-[18px] font-bold tracking-[-0.02em]">
                1Transcribe
              </span>
            </a>

            <p className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-bold uppercase tracking-[0.16em] text-[var(--tint)] lg:mt-14">
              <span className="inline-flex h-2 w-2 rounded-full bg-[var(--tint)]" />
              {t("downloadPage.eyebrow", { platform: platformName })}
            </p>
            <h1 className="mt-4 text-[clamp(2.4rem,4.4vw,3.8rem)] font-bold leading-[1.02] tracking-[-0.045em]">
              {t("downloadPage.title")}
            </h1>
            <p className="mt-4 max-w-[460px] text-[16px] leading-7 text-[var(--text-secondary)]">
              <Trans
                i18nKey="downloadPage.subtitle"
                values={{ email }}
                components={{
                  strong: (
                    <strong className="font-semibold text-[var(--text-primary)]" />
                  ),
                }}
              />
            </p>

            <ol className="mt-10 space-y-1" aria-label={t("downloadPage.stepsLabel")}>
              {steps.map((step, index) => {
                const number = (index + 1) as WalkthroughStep;
                const state =
                  number === activeStep
                    ? "active"
                    : number < activeStep
                      ? "done"
                      : "todo";
                return (
                  <li key={`${os}-${number}`}>
                    <button
                      type="button"
                      data-action-id="acquisition.desktop.showInstallStep"
                      aria-current={state === "active" ? "step" : undefined}
                      onClick={() => walkthrough.jump(number)}
                      className="-mx-3 grid w-[calc(100%+1.5rem)] grid-cols-[36px_minmax(0,1fr)] items-start gap-4 rounded-[var(--r-xl)] px-3 py-3.5 text-left transition-colors duration-[200ms] hover:bg-[var(--fill-1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-[14px] font-bold transition-colors duration-[240ms] ${
                          state === "active"
                            ? "bg-[var(--accent)] text-[var(--text-inverse)] shadow-[0_10px_20px_-12px_var(--accent-glow)]"
                            : state === "done"
                              ? "bg-[var(--success)]/20 text-[var(--success)]"
                              : "bg-[var(--fill-2)] text-[var(--text-secondary)]"
                        }`}
                      >
                        {state === "done" ? <CheckIcon size={15} /> : number}
                      </span>
                      <span className="min-w-0 pt-1.5">
                        <span
                          className={`block text-[17px] font-semibold leading-6 tracking-[-0.01em] transition-colors duration-[240ms] ${
                            state === "todo"
                              ? "text-[var(--text-secondary)]"
                              : "text-[var(--text-primary)]"
                          }`}
                        >
                          {step.title}
                        </span>
                        <span
                          className={`mt-1 block text-[14px] leading-6 transition-colors duration-[240ms] [&_strong]:font-semibold ${
                            state === "todo"
                              ? "text-[var(--text-muted)] [&_strong]:text-[var(--text-secondary)]"
                              : "text-[var(--text-secondary)] [&_strong]:text-[var(--text-primary)]"
                          }`}
                        >
                          {step.detail}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-[var(--hairline)] pt-6 text-[14px] text-[var(--text-secondary)]">
              <span>
                {t("downloadPage.notStarted")}{" "}
                <a
                  href={url}
                  className="font-semibold text-[var(--tint)] underline decoration-[var(--tint-soft)] underline-offset-4 hover:text-[var(--tint-strong)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--focus-ring)]"
                >
                  {t("downloadPage.tryAgain")}
                </a>
              </span>
              <button
                data-action-id="acquisition.desktop.switchInstaller"
                type="button"
                className="btn btn-glass min-h-[var(--control-sm)] px-4 text-[13px]"
                onClick={() => setOs(os === "mac" ? "windows" : "mac")}
              >
                {t("downloadPage.switchInstaller", { platform: otherPlatformName })}
              </button>
            </div>
          </div>
        </section>

        {/* The rehearsal is the other half of the page: a desktop, edge to
            edge, with the windows floating on it. It stays put while the
            steps scroll. */}
        <section className="relative order-first h-[calc(75vw+128px)] min-h-[420px] overflow-hidden lg:sticky lg:top-0 lg:order-none lg:h-[100svh] lg:min-h-0">
          <InstallStage
            key={os}
            os={os}
            frame={walkthrough.frame}
            fileName={fileName}
            email={email}
          />
        </section>
      </div>
    </main>
  );
}

// React advances one phase at a time from the timeline; CSS transitions own
// the motion between phases. With reduced motion the stage shows each step's
// resting frame and only moves when a step is chosen.
const useWalkthrough = (os: DesktopOs) => {
  const reduced = useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const [play, setPlay] = useState(() => ({ os, origin: performance.now() }));
  const [still, setStill] = useState<WalkthroughStep>(1);
  const [frame, setFrame] = useState<WalkthroughFrame>(() =>
    reduced ? walkthroughStillFrame(os, 1) : walkthroughFrame(os, 0),
  );
  if (play.os !== os) {
    setPlay({ os, origin: performance.now() });
    setStill(1);
  }

  useEffect(() => {
    if (reduced) {
      setFrame(walkthroughStillFrame(play.os, still));
      return;
    }
    let timer = 0;
    const tick = () => {
      const next = walkthroughFrame(play.os, performance.now() - play.origin);
      setFrame(next);
      timer = window.setTimeout(tick, next.next);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [play, reduced, still]);

  const jump = (step: WalkthroughStep) => {
    if (reduced) setStill(step);
    else
      setPlay({
        os,
        origin: performance.now() - walkthroughStepStart(os, step),
      });
  };

  return { frame, jump };
};

// ---------------------------------------------------------------------------
// The stage: a 640×480 desktop, scaled to its container.

const STAGE_W = 640;
const STAGE_H = 480;

const stageCaption = (
  t: TFunction,
  os: DesktopOs,
  phase: WalkthroughPhase,
  fileName: string,
): string => {
  const caption = (name: string, values?: Record<string, string>) =>
    t(`downloadPage.caption.${name}`, values);
  switch (phase) {
    case "download.progress":
      return caption("downloading", { fileName });
    case "download.done":
    case "download.aim":
    case "download.click":
      return caption("clickFile");
    case "install.window":
    case "install.aim":
      return caption("installerWindow");
    case "install.grab":
    case "install.drag":
    case "install.hover":
    case "install.drop":
      return caption("drag");
    case "install.copying":
    case "install.copied":
      return caption("copying");
    case "install.setup":
    case "install.installing":
      return caption("setup");
    case "install.installed":
      return caption("installed");
    case "open.gatekeeper":
    case "open.gatekeeper.aim":
    case "open.gatekeeper.click":
      return caption("gatekeeper");
    case "open.app":
    case "open.signIn.aim":
    case "open.signIn.click":
      return caption(os === "mac" ? "signInMac" : "signInWindows");
    case "open.connected":
      return caption("connected");
  }
};

// Space the desktop keeps clear of the windows: the menu bar or taskbar, and
// the caption pill along the bottom.
const DESKTOP_TOP = { mac: 28, windows: 0 };
const DESKTOP_BOTTOM = { mac: 96, windows: 144 };

function InstallStage({
  os,
  frame,
  fileName,
  email,
}: {
  os: DesktopOs;
  frame: WalkthroughFrame;
  fileName: string;
  email: string;
}) {
  const { t } = useTranslation();
  const desktop = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ scale: number; left: number; top: number }>();
  useEffect(() => {
    const node = desktop.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const room = height - DESKTOP_TOP[os] - DESKTOP_BOTTOM[os];
      const scale = Math.min(width / STAGE_W, room / STAGE_H);
      setBox({
        scale,
        left: (width - STAGE_W * scale) / 2,
        top: DESKTOP_TOP[os] + (room - STAGE_H * scale) / 2,
      });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [os]);

  const { phase, step } = frame;
  const cursor = cursorFor(os, phase);
  const caption = stageCaption(t, os, phase, fileName);

  return (
    <div ref={desktop} aria-hidden="true" className="iw absolute inset-0 overflow-hidden">
      <style>{STAGE_CSS}</style>
      <Wallpaper os={os} />
      {os === "mac" ? <MacMenuBar /> : null}

      {box && (
        <div
          className="absolute"
          style={{
            width: STAGE_W,
            height: STAGE_H,
            left: box.left,
            top: box.top,
            transform: `scale(${box.scale})`,
            transformOrigin: "top left",
          }}
        >
          <BrowserScene
            os={os}
            phase={phase}
            fileName={fileName}
            behind={step !== 1}
          />
          {os === "mac" ? (
            <>
              <FinderDmgScene phase={phase} on={step >= 2} behind={step === 3} />
              <GatekeeperDialog phase={phase} />
            </>
          ) : (
            <WindowsSetupScene phase={phase} on={step === 2} />
          )}
          <FirstRunScene os={os} phase={phase} email={email} />
          <Cursor os={os} {...cursor} />
        </div>
      )}

      {os === "windows" ? <WindowsTaskbar appInstalled={step === 3} /> : null}

      <div
        className="absolute inset-x-0 flex justify-center px-6"
        style={{ bottom: os === "windows" ? 64 : 28 }}
      >
        <div className="flex max-w-full items-center gap-4 rounded-full bg-[rgba(10,12,30,0.5)] py-3 pl-5 pr-6 text-white shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-xl">
          <div className="flex shrink-0 gap-1.5">
            {([1, 2, 3] as const).map((segment) => (
              <span
                key={segment}
                className="relative h-1.5 w-7 overflow-hidden rounded-full bg-white/25 sm:w-9"
              >
                {segment === step ? (
                  <span
                    key={phase}
                    className="iw-segment absolute inset-0 rounded-full bg-white"
                    style={{
                      ["--iw-from" as string]: frame.stepProgress,
                      ["--iw-to" as string]: frame.stepProgressEnd,
                      ["--iw-ms" as string]: `${frame.next}ms`,
                    }}
                  />
                ) : (
                  <span
                    className="absolute inset-0 origin-left rounded-full bg-white transition-transform duration-200"
                    style={{ transform: `scaleX(${segment < step ? 1 : 0})` }}
                  />
                )}
              </span>
            ))}
          </div>
          <p
            key={caption}
            className="iw-caption min-w-0 truncate text-[13px] font-medium"
          >
            {caption}
          </p>
        </div>
      </div>
    </div>
  );
}

// Every motion the stage makes. Phases toggle `data-*` attributes; these rules
// do the rest, so the timeline stays a table and the scenes stay declarative.
const STAGE_CSS = `
.iw { font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif; -webkit-font-smoothing: antialiased; }
.iw-scene { position: absolute; inset: 0; transition: opacity 340ms var(--ease-out); }
.iw-scene[data-on="false"] { opacity: 0; pointer-events: none; }
.iw-scene { transition: opacity 340ms var(--ease-out), filter 500ms ease; }
.iw-scene[data-behind="true"] { filter: brightness(0.86) saturate(0.9); }
.iw-win { position: absolute; transform-origin: 50% 40%; transition: opacity 240ms var(--ease-out), transform 360ms var(--ease-out); }
.iw-win[data-on="false"] { opacity: 0; transform: scale(0.94); pointer-events: none; }
.iw-pop { animation: iw-pop 280ms var(--ease-out) both; }
@keyframes iw-pop { from { opacity: 0; transform: translateY(6px) scale(0.97); } to { opacity: 1; transform: none; } }
.iw-fill { transform-origin: left; animation: iw-fill var(--iw-ms, 1500ms) var(--ease-out) forwards; }
@keyframes iw-fill { from { transform: scaleX(0.02); } to { transform: scaleX(1); } }
.iw-ring { stroke-dasharray: 100; animation: iw-ring var(--iw-ms, 1500ms) cubic-bezier(0.4, 0, 0.6, 1) forwards; }
@keyframes iw-ring { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
.iw-cursor { position: absolute; left: 0; top: 0; z-index: 40; pointer-events: none; transition: transform 820ms var(--ease-glide), opacity 200ms; }
.iw-cursor > span { display: block; transition: transform 110ms ease; transform-origin: 4px 3px; }
.iw-cursor[data-pressed="true"] > span { transform: scale(0.82); }
.iw-cursor svg { display: block; overflow: visible; paint-order: stroke fill; stroke-width: 1.6px; stroke-linejoin: round; }
.iw-cursor[data-os="mac"] svg { fill: #000; stroke: #fff; }
.iw-cursor[data-os="windows"] svg { fill: #fff; stroke: #000; }
.iw-cursor i { position: absolute; left: -9px; top: -10px; width: 26px; height: 26px; border-radius: 999px; box-shadow: 0 0 0 2px rgba(1, 86, 249, 0.55); opacity: 0; }
.iw-cursor[data-pressed="true"] i { animation: iw-click 460ms ease-out both; }
@keyframes iw-click { 0% { opacity: 0.8; transform: scale(0.5); } 100% { opacity: 0; transform: scale(1.5); } }
.iw-ghost { transition: transform 1400ms var(--ease-glide), opacity 220ms ease; }
.iw-folder { transition: filter 200ms ease, background-color 200ms ease; }
.iw-folder[data-hot="true"] { filter: brightness(0.82); }
.iw-press { transition: background-color 120ms ease, transform 120ms ease; }
.iw-press[data-pressed="true"] { transform: scale(0.97); filter: brightness(0.92); }
.iw-segment { transform-origin: left; animation: iw-segment var(--iw-ms) linear forwards; }
@keyframes iw-segment { from { transform: scaleX(var(--iw-from)); } to { transform: scaleX(var(--iw-to)); } }
.iw-caption { animation: iw-caption 320ms var(--ease-out) both; }
@keyframes iw-caption { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .iw *, .iw-caption { animation: none !important; transition: none !important; }
  .iw-fill { transform: none; }
  .iw-segment { transform: scaleX(var(--iw-to)); }
  .iw-ring { stroke-dashoffset: 0; }
}
`;

// ---------------------------------------------------------------------------
// Cursor. Stage coordinates of the arrow tip per phase. macOS ships a black
// arrow with a white rim; Windows the reverse.

type CursorState = { x: number; y: number; pressed?: boolean };

// Measured centres of the click targets in stage space (see the
// `data-iw-target` markers); the browser window sits 16px higher on Windows.
const BUBBLE_FILE = { mac: { x: 372, y: 146 }, windows: { x: 372, y: 130 } };
const DMG_APP = { x: 186, y: 284 };
const DMG_FOLDER = { x: 452, y: 282 };
const GATEKEEPER_OPEN = { x: 316, y: 330 };
const SIGN_IN = { mac: { x: 314, y: 336 }, windows: { x: 314, y: 326 } };

const cursorFor = (os: DesktopOs, phase: WalkthroughPhase): CursorState => {
  switch (phase) {
    case "download.progress":
    case "download.done":
      return { x: 300, y: 330 };
    case "download.aim":
      return BUBBLE_FILE[os];
    case "download.click":
      return { ...BUBBLE_FILE[os], pressed: true };
    case "install.window":
      return { x: 420, y: 200 };
    case "install.aim":
      return DMG_APP;
    case "install.grab":
      return { ...DMG_APP, pressed: true };
    case "install.drag":
    case "install.hover":
      return { ...DMG_FOLDER, pressed: true };
    case "install.drop":
      return DMG_FOLDER;
    case "install.copying":
    case "install.copied":
      return { x: 500, y: 380 };
    case "install.setup":
    case "install.installing":
    case "install.installed":
      return { x: 470, y: 372 };
    case "open.gatekeeper":
      return { x: 470, y: 400 };
    case "open.gatekeeper.aim":
      return GATEKEEPER_OPEN;
    case "open.gatekeeper.click":
      return { ...GATEKEEPER_OPEN, pressed: true };
    case "open.app":
      return os === "mac" ? { x: 420, y: 410 } : { x: 470, y: 372 };
    case "open.signIn.aim":
      return SIGN_IN[os];
    case "open.signIn.click":
      return { ...SIGN_IN[os], pressed: true };
    case "open.connected":
      return { x: 470, y: 392 };
  }
};

function Cursor({ os, x, y, pressed = false }: { os: DesktopOs } & CursorState) {
  return (
    <div
      className="iw-cursor"
      data-os={os}
      data-pressed={pressed}
      style={{ transform: `translate(${x - 4}px, ${y - 3}px)` }}
    >
      <span>
        <CursorIcon size={22} />
      </span>
      <i />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop dressing.

function Wallpaper({ os }: { os: DesktopOs }) {
  return (
    <div
      className="absolute inset-0"
      style={{
        background:
          os === "mac"
            ? "radial-gradient(circle at 18% 22%, rgba(1,86,249,0.55), transparent 48%), radial-gradient(circle at 82% 18%, rgba(150,76,214,0.5), transparent 46%), radial-gradient(circle at 70% 88%, rgba(22,162,214,0.55), transparent 44%), radial-gradient(circle at 20% 90%, rgba(255,160,215,0.45), transparent 42%), linear-gradient(160deg, #0f1a48 0%, #1a1650 55%, #0c2a5e 100%)"
            : "radial-gradient(circle at 50% 60%, rgba(120,190,255,0.75), transparent 38%), radial-gradient(circle at 30% 30%, rgba(1,86,249,0.6), transparent 48%), radial-gradient(circle at 78% 30%, rgba(150,76,214,0.4), transparent 44%), linear-gradient(180deg, #0a2a6a 0%, #0d3f9a 60%, #082253 100%)",
      }}
    />
  );
}

const clock = (): string =>
  new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function MacMenuBar() {
  const { t } = useTranslation();
  return (
    <div
      className="absolute inset-x-0 top-0 z-10 flex h-7 items-center gap-4 px-4 text-[13px] font-medium text-[color:light-dark(#1d1d1f,#f5f5f7)]"
      style={{
        background: "light-dark(rgba(255,255,255,0.5), rgba(30,30,36,0.55))",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
      }}
    >
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
      </svg>
      <span className="font-bold">{t("downloadPage.os.finder")}</span>
      {(["File", "Edit", "View", "Go", "Window", "Help"] as const).map((menu) => (
        <span key={menu} className="hidden sm:inline">
          {t(`downloadPage.os.menu${menu}`)}
        </span>
      ))}
      <span className="ml-auto flex items-center gap-3 text-[11px]">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
          <path d="M12 18.5a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5Zm-4.24-4.24a6 6 0 0 1 8.48 0l-1.41 1.41a4 4 0 0 0-5.66 0l-1.41-1.41Zm-3.54-3.54a11 11 0 0 1 15.56 0l-1.42 1.42a9 9 0 0 0-12.72 0L4.22 10.72ZM.7 7.2a16 16 0 0 1 22.6 0l-1.42 1.42a14 14 0 0 0-19.76 0L.7 7.2Z" />
        </svg>
        <span className="inline-flex items-center gap-1">
          <span className="relative h-[10px] w-[22px] rounded-[3px] ring-1 ring-current/70">
            <span className="absolute inset-[1.5px] right-[3px] rounded-[1.5px] bg-current" />
          </span>
        </span>
        <span>{clock()}</span>
      </span>
    </div>
  );
}

function WindowsTaskbar({ appInstalled }: { appInstalled: boolean }) {
  return (
    <div
      className="absolute inset-x-0 bottom-0 z-10 flex h-12 items-center justify-center gap-2 px-3 text-[color:light-dark(#1b1b1b,#f3f3f3)]"
      style={{
        background: "light-dark(rgba(243,243,243,0.86), rgba(32,32,32,0.86))",
        backdropFilter: "blur(24px) saturate(160%)",
        WebkitBackdropFilter: "blur(24px) saturate(160%)",
        boxShadow: "inset 0 1px 0 light-dark(rgba(255,255,255,0.6), rgba(255,255,255,0.06))",
      }}
    >
      <TaskbarSlot>
        <svg viewBox="0 0 24 24" className="h-4 w-4">
          <path fill="#0a84ff" d="M3 3h8.5v8.5H3zM12.5 3H21v8.5h-8.5zM3 12.5h8.5V21H3zM12.5 12.5H21V21h-8.5z" />
        </svg>
      </TaskbarSlot>
      <TaskbarSlot>
        <span className="h-4 w-4 rounded-full ring-[1.5px] ring-current/70">
          <span className="absolute -bottom-0.5 -right-0.5 h-1.5 w-[2px] rotate-[-45deg] bg-current" />
        </span>
      </TaskbarSlot>
      <TaskbarSlot>
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.6">
          <rect x="3" y="5" width="18" height="14" rx="2" />
        </svg>
      </TaskbarSlot>
      <TaskbarSlot active>
        <span className="h-4 w-4 rounded-full bg-[conic-gradient(#4285f4,#34a853,#fbbc05,#ea4335,#4285f4)] ring-2 ring-[color:light-dark(#f3f3f3,#202020)] ring-inset">
          <span className="absolute inset-[5px] rounded-full bg-[#4285f4] ring-2 ring-white" />
        </span>
      </TaskbarSlot>
      {appInstalled ? (
        <TaskbarSlot active>
          <img src="/logo.png" alt="" className="h-5 w-5 rounded-[5px]" />
        </TaskbarSlot>
      ) : null}
      <span className="absolute right-3 text-[11px] leading-tight text-right">
        {clock()}
      </span>
    </div>
  );
}

function TaskbarSlot({ children, active = false }: { children: ReactNode; active?: boolean }) {
  return (
    <span className="relative flex h-8 w-8 items-center justify-center rounded-[6px]">
      {children}
      {active ? (
        <span className="absolute bottom-0 left-1/2 h-[3px] w-4 -translate-x-1/2 rounded-full bg-[#0067c0]" />
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// OS window chrome.

function MacTitleBar({ title, icon }: { title: string; icon?: ReactNode }) {
  return (
    <div
      className="relative flex h-7 shrink-0 items-center justify-center text-[12px] font-semibold text-[color:light-dark(#3a3a3c,#e5e5ea)]"
      style={{
        background: "light-dark(#f2f2f4, #323236)",
        boxShadow: "inset 0 -1px 0 light-dark(rgba(0,0,0,0.12), rgba(255,255,255,0.06))",
      }}
    >
      <span className="absolute left-3 top-1/2 flex -translate-y-1/2 gap-2">
        <span className="h-3 w-3 rounded-full bg-[#ff5f57] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
        <span className="h-3 w-3 rounded-full bg-[#febc2e] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
        <span className="h-3 w-3 rounded-full bg-[#28c840] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
      </span>
      <span className="inline-flex items-center gap-1.5">
        {icon}
        {title}
      </span>
    </div>
  );
}

function WindowsTitleBar({ title, icon }: { title: string; icon?: ReactNode }) {
  return (
    <div
      className="flex h-8 shrink-0 items-center gap-2 pl-3 text-[12px] text-[color:light-dark(#1b1b1b,#ffffff)]"
      style={{ background: "light-dark(#f3f3f3, #202020)" }}
    >
      {icon}
      <span className="flex-1 truncate">{title}</span>
      <span className="flex h-full items-stretch text-[10px]">
        <span className="flex w-11 items-center justify-center">
          <span className="h-px w-[10px] bg-current" />
        </span>
        <span className="flex w-11 items-center justify-center">
          <span className="h-[10px] w-[10px] rounded-[1.5px] ring-1 ring-current" />
        </span>
        <span className="flex w-11 items-center justify-center">
          <svg viewBox="0 0 10 10" className="h-[10px] w-[10px] stroke-current" strokeWidth="1.1">
            <path d="M1 1l8 8M9 1l-8 8" />
          </svg>
        </span>
      </span>
    </div>
  );
}

const macWindowShadow =
  "0 0 0 0.5px light-dark(rgba(0,0,0,0.22), rgba(255,255,255,0.14)), 0 22px 60px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.18)";
const winWindowShadow =
  "0 0 0 1px light-dark(rgba(0,0,0,0.16), rgba(255,255,255,0.12)), 0 16px 48px rgba(0,0,0,0.42)";

function AppIcon({
  size,
  className = "",
  ...rest
}: { size: number; className?: string } & Record<`data-${string}`, string>) {
  return (
    <img
      src="/logo.png"
      alt=""
      width={size}
      height={size}
      className={`shrink-0 rounded-[22.5%] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.18),0_0_0_0.5px_rgba(0,0,0,0.08)] ${className}`}
      {...rest}
    />
  );
}

function DiskImageIcon({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className="shrink-0">
      <rect x="3" y="7" width="26" height="18" rx="3" fill="#f5f5f7" stroke="#b8b8bd" />
      <rect x="6" y="10" width="20" height="9" rx="1.5" fill="#dcdce1" />
      <circle cx="24" cy="22" r="1.4" fill="#34c759" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — the browser and its download bubble.

// The browser stays open underneath everything that follows, as it does on a
// real desktop; only its download bubble belongs to step 1.
function BrowserScene({
  os,
  phase,
  fileName,
  behind,
}: {
  os: DesktopOs;
  phase: WalkthroughPhase;
  fileName: string;
  behind: boolean;
}) {
  const { t } = useTranslation();
  const mac = os === "mac";
  const downloading = phase === "download.progress";
  const bubble =
    phase === "download.done" ||
    phase === "download.aim" ||
    phase === "download.click";
  const pressed = phase === "download.click";
  const top = mac ? 36 : 20;
  return (
    <div className="iw-scene" data-on="true" data-behind={behind}>
      <div
        className={`absolute flex flex-col overflow-hidden ${mac ? "rounded-[10px]" : "rounded-[8px]"}`}
        style={{
          left: 20,
          top,
          width: 600,
          height: 420,
          background: "light-dark(#ffffff, #202124)",
          boxShadow: mac ? macWindowShadow : winWindowShadow,
        }}
      >
        <div
          className="relative flex h-9 shrink-0 items-end px-2"
          style={{ background: "light-dark(#dee1e6, #202124)" }}
        >
          {mac ? (
            <span className="mb-[10px] ml-1 mr-3 flex gap-2">
              <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
              <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
              <span className="h-3 w-3 rounded-full bg-[#28c840]" />
            </span>
          ) : null}
          <span
            className="flex h-[30px] w-[190px] items-center gap-2 rounded-t-[8px] px-3 text-[11px] text-[color:light-dark(#1f1f1f,#e8eaed)]"
            style={{ background: "light-dark(#ffffff, #35363a)" }}
          >
            <img src="/logo.png" alt="" className="h-3.5 w-3.5 rounded-[3px]" />
            <span className="truncate">{t("downloadPage.os.tabTitle")}</span>
            <span className="ml-auto text-[13px] leading-none text-[color:light-dark(#5f6368,#9aa0a6)]">
              ×
            </span>
          </span>
          <span className="mb-2 ml-2 text-[16px] leading-none text-[color:light-dark(#5f6368,#9aa0a6)]">
            +
          </span>
          {!mac ? (
            <span className="absolute right-0 top-0 flex h-8 text-[color:light-dark(#1b1b1b,#e8eaed)]">
              <span className="flex w-11 items-center justify-center">
                <span className="h-px w-[10px] bg-current" />
              </span>
              <span className="flex w-11 items-center justify-center">
                <span className="h-[10px] w-[10px] rounded-[1.5px] ring-1 ring-current" />
              </span>
              <span className="flex w-11 items-center justify-center">
                <svg viewBox="0 0 10 10" className="h-[10px] w-[10px] stroke-current" strokeWidth="1.1">
                  <path d="M1 1l8 8M9 1l-8 8" />
                </svg>
              </span>
            </span>
          ) : null}
        </div>

        <div
          className="relative flex h-10 shrink-0 items-center gap-1 px-2 text-[color:light-dark(#5f6368,#9aa0a6)]"
          style={{
            background: "light-dark(#ffffff, #35363a)",
            boxShadow: "inset 0 -1px 0 light-dark(rgba(0,0,0,0.08), rgba(255,255,255,0.06))",
          }}
        >
          <ToolbarGlyph>
            <path d="M15 5l-7 7 7 7" />
          </ToolbarGlyph>
          <ToolbarGlyph muted>
            <path d="M9 5l7 7-7 7" />
          </ToolbarGlyph>
          <ToolbarGlyph>
            <path d="M19 12a7 7 0 1 1-2.05-4.95M19 4v4h-4" />
          </ToolbarGlyph>
          <span
            className="ml-1 flex h-7 flex-1 items-center gap-2 rounded-full px-3 text-[11.5px] text-[color:light-dark(#1f1f1f,#e8eaed)]"
            style={{ background: "light-dark(#f1f3f4, #282a2d)" }}
          >
            <svg viewBox="0 0 24 24" className="h-3 w-3 fill-none stroke-current" strokeWidth="2">
              <rect x="5" y="10" width="14" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            app.1transcribe.com/download
          </span>

          <span className="relative ml-1 flex h-8 w-8 items-center justify-center rounded-full">
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14" />
            </svg>
            {downloading ? (
              <svg viewBox="0 0 32 32" className="absolute inset-0 h-8 w-8 -rotate-90">
                <circle cx="16" cy="16" r="14" fill="none" stroke="light-dark(#dadce0, #3c4043)" strokeWidth="2.5" />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  pathLength={100}
                  fill="none"
                  stroke="#1a73e8"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="iw-ring"
                  style={{ ["--iw-ms" as string]: "1600ms" }}
                />
              </svg>
            ) : (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#1e8e3e] text-white ring-2 ring-[color:light-dark(#ffffff,#35363a)]">
                <CheckIcon size={8} />
              </span>
            )}
          </span>
          <span className="flex h-8 w-8 items-center justify-center">
            <span className="h-[18px] w-[18px] rounded-full bg-[color:light-dark(#c2e7ff,#004a77)] text-[10px] font-bold leading-[18px] text-center text-[color:light-dark(#001d35,#c2e7ff)]">
              {os === "mac" ? "M" : "W"}
            </span>
          </span>
          <ToolbarGlyph>
            <path d="M12 6h.01M12 12h.01M12 18h.01" strokeWidth="3" />
          </ToolbarGlyph>

          {bubble ? (
            <div
              className="iw-pop absolute right-[70px] top-[42px] z-20 w-[300px] overflow-hidden rounded-[12px] text-[color:light-dark(#1f1f1f,#e8eaed)]"
              style={{
                background: "light-dark(#ffffff, #292a2d)",
                boxShadow: "0 0 0 1px light-dark(rgba(0,0,0,0.08), rgba(255,255,255,0.1)), 0 8px 24px rgba(0,0,0,0.28)",
              }}
            >
              <div
                className="iw-press flex items-center gap-3 px-4 py-3"
                data-iw-target="bubble"
                data-pressed={pressed}
                style={{
                  background:
                    phase === "download.aim" || pressed
                      ? "light-dark(rgba(0,0,0,0.05), rgba(255,255,255,0.08))"
                      : "transparent",
                }}
              >
                {os === "mac" ? <DiskImageIcon size={28} /> : <AppIcon size={28} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-medium">
                    {fileName}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-[color:light-dark(#5f6368,#9aa0a6)]">
                    {t("downloadPage.os.done")}
                  </span>
                </span>
                <span className="text-[16px] leading-none text-[color:light-dark(#5f6368,#9aa0a6)]">
                  ⋮
                </span>
              </div>
              <div
                className="px-4 py-2.5 text-[11px] font-medium text-[#1a73e8]"
                style={{ boxShadow: "inset 0 1px 0 light-dark(rgba(0,0,0,0.08), rgba(255,255,255,0.08))" }}
              >
                {t("downloadPage.os.showAllDownloads")}
              </div>
            </div>
          ) : null}
        </div>

        <MiniDownloadPage />
      </div>
    </div>
  );
}

function ToolbarGlyph({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
  return (
    <span className={`flex h-8 w-8 items-center justify-center ${muted ? "opacity-40" : ""}`}>
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </span>
  );
}

// The page behind the bubble is this page, at a glance: same field, same
// shape, so the rehearsal sits on the user's actual screen.
function MiniDownloadPage() {
  return (
    <div className="app-page-background relative flex-1 overflow-hidden bg-[var(--bg-primary)]">
      <div className="absolute left-8 top-7 flex items-center gap-2">
        <img src="/logo.png" alt="" className="h-5 w-5 rounded-[6px]" />
        <span className="text-[9px] font-bold text-[var(--text-primary)]">1Transcribe</span>
      </div>
      <div className="absolute left-8 top-[68px] space-y-2.5">
        <span className="block h-1.5 w-16 rounded-full bg-[var(--tint)] opacity-70" />
        <span className="block h-4 w-44 rounded-full bg-[var(--text-primary)] opacity-80" />
        <span className="block h-4 w-32 rounded-full bg-[var(--text-primary)] opacity-80" />
        <span className="mt-4 block h-2 w-52 rounded-full bg-[var(--text-secondary)] opacity-40" />
        <span className="block h-2 w-40 rounded-full bg-[var(--text-secondary)] opacity-40" />
      </div>
      <div
        className="absolute inset-y-0 right-0 w-[46%]"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(1,86,249,0.55), transparent 50%), radial-gradient(circle at 80% 85%, rgba(22,162,214,0.5), transparent 46%), linear-gradient(160deg, #0f1a48, #1a1650 55%, #0c2a5e)",
        }}
      >
        <span className="absolute left-[12%] top-[22%] h-[56%] w-[76%] rounded-[6px] bg-white/90 shadow-lg" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 (Mac) — the DMG window, exactly as electron-builder lays it out:
// 540×380, a dashed arrow, the app at (130, 220) and Applications at (410, 220).

const DMG = { left: 50, top: 44, width: 540, height: 380 };

function FinderDmgScene({
  phase,
  on,
  behind,
}: {
  phase: WalkthroughPhase;
  on: boolean;
  behind: boolean;
}) {
  const { t } = useTranslation();
  const dragging =
    phase === "install.grab" || phase === "install.drag" || phase === "install.hover";
  const ghostAtFolder =
    phase === "install.drag" || phase === "install.hover" || phase === "install.drop";
  const hot = phase === "install.hover" || phase === "install.drop";
  const copying = phase === "install.copying";
  const copied = phase === "install.copied";
  return (
    <div className="iw-scene" data-on={on} data-behind={behind}>
      <div
        className="iw-win flex flex-col overflow-hidden rounded-[10px]"
        data-on={on}
        style={{
          left: DMG.left,
          top: DMG.top,
          width: DMG.width,
          height: DMG.height + 28,
          boxShadow: macWindowShadow,
        }}
      >
        <MacTitleBar title="1Transcribe" icon={<DiskImageIcon size={16} />} />
        <div className="relative flex-1 bg-[#edecec] text-[#1d1d1f]">
          <svg
            viewBox="0 0 52 60"
            className="absolute"
            style={{ left: 242, top: 194, width: 52, height: 60 }}
            fill="none"
            stroke="#949393"
            strokeWidth="1"
            strokeDasharray="3 2"
          >
            <path d="M1 16h20V1l30 29-30 29V44H1z" />
          </svg>

          <DmgItem x={130} y={220} label="1Transcribe">
            <AppIcon size={80} data-iw-target="dmg-app" />
          </DmgItem>
          <DmgItem x={410} y={220} label={t("downloadPage.os.applications")}>
            <span className="iw-folder block rounded-[8px]" data-hot={hot} data-iw-target="dmg-folder">
              <FolderIcon size={80} />
            </span>
          </DmgItem>

          <div
            className="iw-ghost absolute z-20"
            style={{
              left: 130 - 40,
              top: 220 - 40,
              transform: ghostAtFolder ? "translate(280px, 0)" : "translate(0, 0)",
              opacity: dragging ? 0.6 : 0,
            }}
          >
            <AppIcon size={80} />
            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#34c759] text-[14px] font-bold leading-none text-white ring-2 ring-white">
              +
            </span>
          </div>

          {copying || copied ? (
            <div
              className="iw-pop absolute left-[100px] top-[128px] w-[340px] overflow-hidden rounded-[10px]"
              style={{
                background: "light-dark(#ececec, #2b2b2d)",
                boxShadow: macWindowShadow,
                opacity: copied ? 0 : 1,
                transition: "opacity 240ms ease",
              }}
            >
              <div className="flex items-center gap-3 px-4 py-3 text-[color:light-dark(#1d1d1f,#f5f5f7)]">
                <AppIcon size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-semibold">
                    {t("downloadPage.os.copying")}
                  </span>
                  <span className="mt-2 block h-[5px] overflow-hidden rounded-full bg-[color:light-dark(#d2d2d6,#4a4a4e)]">
                    <span
                      className="iw-fill block h-full rounded-full bg-[#0a60ff]"
                      style={{ ["--iw-ms" as string]: "1150ms" }}
                    />
                  </span>
                  <span className="mt-1.5 block text-[10px] text-[color:light-dark(#6e6e73,#a1a1a6)]">
                    {t("downloadPage.os.copyRemaining")}
                  </span>
                </span>
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[color:light-dark(#c9c9ce,#5a5a5e)] text-[10px] leading-none text-[color:light-dark(#4a4a4e,#e5e5ea)]">
                  ×
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DmgItem({
  x,
  y,
  label,
  children,
}: {
  x: number;
  y: number;
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="absolute flex w-[120px] flex-col items-center gap-2"
      style={{ left: x - 60, top: y - 40 }}
    >
      {children}
      <span className="text-[12px] font-medium">{label}</span>
    </div>
  );
}

function FolderIcon({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 80 64" width={size} height={size * 0.8} className="block">
      <path
        d="M4 11a5 5 0 0 1 5-5h20.5l6 6H71a5 5 0 0 1 5 5v36a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V11z"
        fill="#5aa3ec"
      />
      <path
        d="M4 21h72v32a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V21z"
        fill="#8dc6ff"
      />
      <path
        d="M4 21h72v6H4z"
        fill="#7cbaf8"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Step 2 (Windows) — electron-builder's one-click installer: one small window,
// one progress bar, no choices, and the app opens itself when it is done.

function WindowsSetupScene({ phase, on }: { phase: WalkthroughPhase; on: boolean }) {
  const { t } = useTranslation();
  const installing = phase === "install.installing" || phase === "install.installed";
  return (
    <div className="iw-scene" data-on={on}>
      <div
        className="iw-win flex flex-col overflow-hidden rounded-[8px]"
        data-on={on && phase !== "install.installed"}
        style={{
          left: 120,
          top: 150,
          width: 400,
          height: 172,
          background: "light-dark(#f9f9f9, #2b2b2b)",
          boxShadow: winWindowShadow,
        }}
      >
        <WindowsTitleBar
          title={t("downloadPage.os.setupTitle")}
          icon={<img src="/logo.png" alt="" className="h-4 w-4 rounded-[3px]" />}
        />
        <div className="flex flex-1 items-center gap-5 px-6 text-[color:light-dark(#1b1b1b,#ffffff)]">
          <AppIcon size={48} className="rounded-[10px]" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold">{t("downloadPage.os.installing")}</p>
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-[color:light-dark(#dcdcdc,#4a4a4a)]">
              {installing ? (
                <span
                  className="iw-fill block h-full rounded-full bg-[#0067c0]"
                  style={{ ["--iw-ms" as string]: "2400ms" }}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — macOS's one-time question for downloaded apps, then the app's own
// first-run screen (pages/desktop-sign-in.tsx), and the account connecting.

function GatekeeperDialog({ phase }: { phase: WalkthroughPhase }) {
  const { t } = useTranslation();
  const on =
    phase === "open.gatekeeper" ||
    phase === "open.gatekeeper.aim" ||
    phase === "open.gatekeeper.click";
  const pressed = phase === "open.gatekeeper.click";
  return (
    <div
      className="iw-win z-30 flex flex-col items-center rounded-[12px] px-4 pb-4 pt-5 text-center text-[color:light-dark(#1d1d1f,#f5f5f7)]"
      data-on={on}
      style={{
        left: 190,
        top: 104,
        width: 260,
        background: "light-dark(rgba(236,236,238,0.96), rgba(44,44,46,0.96))",
        backdropFilter: "blur(30px)",
        WebkitBackdropFilter: "blur(30px)",
        boxShadow: macWindowShadow,
      }}
    >
      <AppIcon size={60} />
      <p className="mt-4 text-[13px] font-bold leading-[17px]">
        {t("downloadPage.os.gatekeeperTitle")}
      </p>
      <p className="mt-2.5 text-[11px] leading-[14px] text-[color:light-dark(#4a4a4e,#c7c7cc)]">
        {t("downloadPage.os.gatekeeperBody", {
          browser: browserName() ?? t("downloadPage.os.yourBrowser"),
          time: clock(),
        })}
      </p>
      <span
        className="iw-press mt-5 flex h-[30px] w-full items-center justify-center rounded-[7px] bg-[#0a60ff] text-[12.5px] font-semibold text-white shadow-[0_1px_2px_rgba(10,96,255,0.4)]"
        data-iw-target="gatekeeper-open"
        data-pressed={pressed}
      >
        {t("downloadPage.os.open")}
      </span>
      <span
        className="mt-2 flex h-[30px] w-full items-center justify-center rounded-[7px] text-[12.5px] font-medium"
        style={{ background: "light-dark(rgba(0,0,0,0.06), rgba(255,255,255,0.1))" }}
      >
        {t("downloadPage.os.cancel")}
      </span>
    </div>
  );
}

function FirstRunScene({
  os,
  phase,
  email,
}: {
  os: DesktopOs;
  phase: WalkthroughPhase;
  email: string;
}) {
  const { t } = useTranslation();
  const on =
    phase === "open.app" ||
    phase === "open.signIn.aim" ||
    phase === "open.signIn.click" ||
    phase === "open.connected";
  const pressed = phase === "open.signIn.click";
  const connected = phase === "open.connected";
  const mac = os === "mac";
  return (
    <div
      className={`iw-win flex flex-col overflow-hidden ${mac ? "rounded-[10px]" : "rounded-[8px]"}`}
      data-on={on}
      style={{
        left: 60,
        top: mac ? 52 : 40,
        width: 520,
        height: 380,
        boxShadow: mac ? macWindowShadow : winWindowShadow,
      }}
    >
      {mac ? (
        <MacTitleBar title="1Transcribe" />
      ) : (
        <WindowsTitleBar
          title="1Transcribe"
          icon={<img src="/logo.png" alt="" className="h-4 w-4 rounded-[3px]" />}
        />
      )}
      <div className="app-page-background relative flex flex-1 flex-col items-center justify-center bg-[var(--bg-primary)] px-10 text-center text-[var(--text-primary)]">
        <img src="/logo.png" alt="" className="h-14 w-14 rounded-2xl shadow-lg" />
        <p className="mt-4 text-[22px] font-bold tracking-tight">
          {t("desktopSignIn.title")}
        </p>
        <p className="mt-2 max-w-[300px] text-[12.5px] leading-5 text-[var(--text-secondary)]">
          {t("desktopSignIn.subtitle")}
        </p>
        {connected ? (
          <span
            key="connected"
            className="iw-pop glass mt-6 flex h-11 w-[270px] items-center justify-center gap-2.5 rounded-xl px-4 text-[12.5px] font-semibold"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--success)]/20 text-[var(--success)]">
              <CheckIcon size={10} />
            </span>
            <span className="truncate">{t("downloadPage.os.signedInAs", { email })}</span>
          </span>
        ) : (
          <span
            className="iw-press mt-6 flex h-11 w-[270px] items-center justify-center rounded-xl bg-[var(--accent)] text-[13px] font-semibold text-[var(--text-inverse)] shadow-[0_18px_34px_-18px_var(--accent-glow)]"
            data-iw-target="sign-in"
            data-pressed={pressed}
          >
            {pressed ? t("desktopSignIn.opening") : t("desktopSignIn.openBrowser")}
          </span>
        )}
      </div>
    </div>
  );
}
